import {
	Alert,
	Box,
	Button,
	Grid,
	MenuItem,
	Paper,
	Snackbar,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
	UserRole,
	UserWarehouse,
} from "../../../@types/DRO/UserManagementResponse";
import { CreateUserPayload } from "../../../@types/DTO/UserManagementBody";
import { UserManagementApi } from "../../../api/UserManagement/UserManagementApi";
import { useLoading } from "../../../views/Loader/LoadingContext";

interface CreateUserFormValues {
	name: string;
	email: string;
	active: boolean;
	mobile: string;
	roleId: string;
	warehouseId: string;
}

interface FormErrors {
	name?: string;
	email?: string;
	mobile?: string;
	roleId?: string;
	warehouseId?: string;
}

const initialForm: CreateUserFormValues = {
	name: "",
	email: "",
	active: true,
	mobile: "",
	roleId: "",
	warehouseId: "",
};

const formatRoleLabel = (role: string): string => {
	return role
		.replace(/^ROLE_/, "")
		.replace(/_/g, " ");
};

const CreateUser: React.FC = () => {
	const { setLoading } = useLoading();
	const [roles, setRoles] = useState<UserRole[]>([]);
	const [warehouses, setWarehouses] = useState<UserWarehouse[]>([]);
	const [formValues, setFormValues] = useState<CreateUserFormValues>(initialForm);
	const [errors, setErrors] = useState<FormErrors>({});
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error",
	});

	useEffect(() => {
		const fetchInitialData = async () => {
			setLoading(true);
			try {
				const [rolesResponse, warehousesResponse] = await Promise.all([
					UserManagementApi.api.getRoles(),
					UserManagementApi.api.getWarehouses(),
				]);

				setRoles(rolesResponse);
				setWarehouses(warehousesResponse);
			} catch (error: unknown) {
				console.error("Error loading user management options", error);
				setSnackbar({
					open: true,
					message: "Unable to load roles and warehouses",
					severity: "error",
				});
			}
			setLoading(false);
		};

		fetchInitialData();
	}, [setLoading]);

	const selectedRole = useMemo(() => {
		return roles.find((role) => role.id === Number(formValues.roleId));
	}, [roles, formValues.roleId]);

	const shouldShowWarehouse = [
		"ROLE_INVENTORY_MANAGER", 
		"ROLE_FRANCHISE_INVENTORY_MANAGER", 
		"ROLE_ERP_FRANCHISE_ADMIN"
	].includes(selectedRole?.role ?? "");

	const handleFieldChange = (
		field: keyof CreateUserFormValues,
		value: string | boolean
	) => {
		setFormValues((previous) => ({
			...previous,
			[field]: value as never,
			...(field === "roleId" ? { warehouseId: "" } : {}),
		}));

		if (errors[field as keyof FormErrors]) {
			setErrors((previous) => ({ ...previous, [field]: undefined }));
		}
	};

	const validate = (): boolean => {
		const currentErrors: FormErrors = {};

		if (!formValues.name.trim()) {
			currentErrors.name = "Name is required";
		}

		if (!formValues.email.trim()) {
			currentErrors.email = "Email is required";
		} else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formValues.email)) {
			currentErrors.email = "Enter a valid email";
		}

		if (!formValues.mobile.trim()) {
			currentErrors.mobile = "Mobile is required";
		} else if (!/^[0-9]{10}$/.test(formValues.mobile)) {
			currentErrors.mobile = "Mobile must be 10 digits";
		}

		if (!formValues.roleId) {
			currentErrors.roleId = "Role is required";
		}

		if (shouldShowWarehouse && !formValues.warehouseId) {
			currentErrors.warehouseId = "Warehouse is required for inventory manager";
		}

		setErrors(currentErrors);
		return Object.keys(currentErrors).length === 0;
	};

	const handleCreateUser = async () => {
		if (!validate()) {
			return;
		}

		setLoading(true);
		try {
			const payload: CreateUserPayload = {
				name: formValues.name.trim(),
				email: formValues.email.trim(),
				active: formValues.active,
				mobile: formValues.mobile,
				roleId: Number(formValues.roleId),
				...(shouldShowWarehouse ? { warehouseId: Number(formValues.warehouseId) } : {}),
			};

			await UserManagementApi.api.createUser(payload);

			setSnackbar({
				open: true,
				message: "User created successfully",
				severity: "success",
			});
			setFormValues(initialForm);
			setErrors({});
		} catch (error: unknown) {
			console.error("Error creating user", error);
			const message =
				typeof error === "object" &&
				error !== null &&
				"response" in error &&
				typeof (error as { response?: { data?: { message?: string } } }).response?.data
					?.message === "string"
					? (error as { response?: { data?: { message?: string } } }).response?.data
							?.message ?? "Error creating user"
					: "Error creating user";

			setSnackbar({
				open: true,
				message,
				severity: "error",
			});
		}
		setLoading(false);
	};

	const handleCloseSnackbar = () => {
		setSnackbar((previous) => ({ ...previous, open: false }));
	};

	return (
		<Box sx={{ padding: 4 }}>
			<Box sx={{ mb: 3 }}>
				<Typography
					variant="h5"
					gutterBottom
					sx={{
						fontWeight: "600",
						color: "#1E1E1E",
						textAlign: "left",
						fontSize: "1.5rem",
						mb: 1,
					}}>
					Create User
				</Typography>
				<div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
					<Link
						to="/home"
						className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
						Dashboard
					</Link>
					<span className="text-sm text-gray-400">/</span>
					<Link
						to="/user-management/create"
						className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
						User Management
					</Link>
					<span className="text-sm text-gray-400">/</span>
					<span className="text-sm font-medium text-gray-700">Create User</span>
				</div>
			</Box>

			<Paper
				sx={{
					padding: 3,
					marginTop: 2,
					borderRadius: "8px",
					boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
				}}>
				<Grid container spacing={3} sx={{ textAlign: "left" }}>
					<Grid item xs={12} sm={4}>
						<TextField
							label="Name"
							fullWidth
							variant="standard"
							value={formValues.name}
							onChange={(event) => handleFieldChange("name", event.target.value)}
							error={!!errors.name}
							helperText={errors.name}
						/>
					</Grid>

					<Grid item xs={12} sm={4}>
						<TextField
							label="Email"
							fullWidth
							variant="standard"
							value={formValues.email}
							onChange={(event) => handleFieldChange("email", event.target.value)}
							error={!!errors.email}
							helperText={errors.email}
						/>
					</Grid>

					<Grid item xs={12} sm={4}>
						<TextField
							label="Mobile"
							fullWidth
							variant="standard"
							value={formValues.mobile}
							onChange={(event) => {
								if (/^\d{0,10}$/.test(event.target.value)) {
									handleFieldChange("mobile", event.target.value);
								}
							}}
							error={!!errors.mobile}
							helperText={errors.mobile}
							inputProps={{ maxLength: 10 }}
						/>
					</Grid>

					<Grid item xs={12} sm={4}>
						<TextField
							select
							label="Role"
							fullWidth
							variant="standard"
							value={formValues.roleId}
							onChange={(event) => handleFieldChange("roleId", event.target.value)}
							error={!!errors.roleId}
							helperText={errors.roleId}>
							{roles.map((role) => (
								<MenuItem key={role.id} value={String(role.id)}>
									{formatRoleLabel(role.role)}
								</MenuItem>
							))}
						</TextField>
					</Grid>

					{shouldShowWarehouse && (
						<Grid item xs={12} sm={4}>
							<TextField
								select
								label="Warehouse"
								fullWidth
								variant="standard"
								value={formValues.warehouseId}
								onChange={(event) =>
									handleFieldChange("warehouseId", event.target.value)
								}
								error={!!errors.warehouseId}
								helperText={errors.warehouseId}>
								{warehouses.map((warehouse) => (
									<MenuItem key={warehouse.id} value={String(warehouse.id)}>
										{warehouse.name}
									</MenuItem>
								))}
							</TextField>
						</Grid>
					)}

					<Grid item xs={12} sm={4}>
						<Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
							Status
						</Typography>
						<Box sx={{ display: "flex", alignItems: "center", minHeight: 40 }}>
							<Switch
								checked={formValues.active}
								onChange={(event) =>
									handleFieldChange("active", event.target.checked)
								}
							/>
							<Typography variant="body2">
								{formValues.active ? "Active" : "Inactive"}
							</Typography>
						</Box>
					</Grid>
				</Grid>
			</Paper>

			<Box sx={{ marginTop: 4, textAlign: "right" }}>
				<Button
					variant="contained"
					onClick={handleCreateUser}
					color="primary"
					sx={{
						padding: "10px 20px",
						borderRadius: "32px",
						backgroundColor: "#1E1E1E",
						textTransform: "none",
						fontWeight: "500",
						"&:hover": { backgroundColor: "#000000" },
					}}>
					Create User
				</Button>
			</Box>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={2500}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default CreateUser;
